import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Star, Cloud } from "lucide-react";
import { useApp } from "@/context/AppProviders";

const Pricing = () => {
  const { t } = useApp();
  const p = t.pricing;
  const navigate = useNavigate();
  const [duration, setDuration] = useState("monthly");

  const goShop = () => navigate("/shop");

  const durations = ["monthly", "annual"];

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-24 sm:py-32 border-t border-border"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="max-w-2xl"
        >
          <p className="font-mono text-sm uppercase tracking-widest text-blue-500">
            {p.label}
          </p>
          <h2 className="font-heading mt-3 text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
            {p.title}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            {p.subtitle}
          </p>
        </motion.div>

        {/* Billing duration toggle */}
        <div
          data-testid="billing-toggle"
          className="mt-8 inline-flex rounded-sm border border-border bg-card p-1"
        >
          {durations.map((d) => (
            <button
              key={d}
              onClick={() => setDuration(d)}
              data-testid={`billing-${d}`}
              className={`relative rounded-sm px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                duration === d
                  ? "bg-blue-600 text-white"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              {p.billing[d]}
            </button>
          ))}
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-3 items-start">
          {p.tiers.map((tier, i) => {
            const popular = tier.id === "professional";
            const micro = p.microSub;
            return (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`pricing-tier-${tier.id}`}
                className={`relative flex flex-col rounded-sm border bg-card p-8 transition-colors duration-200 ${
                  popular
                    ? "border-blue-600 shadow-[0_0_0_1px_rgb(37,99,235),0_0_40px_-12px_rgb(37,99,235)] lg:-mt-4 lg:mb-4"
                    : "border-border hover:border-muted-foreground/40"
                }`}
              >
                {popular && (
                  <span
                    data-testid="pricing-popular-badge"
                    className="absolute -top-3 left-8 inline-flex items-center gap-1 rounded-sm bg-blue-600 px-2.5 py-1 text-xs font-medium text-white"
                  >
                    <Star className="h-3 w-3 fill-current" />
                    {p.popular}
                  </span>
                )}

                <h3 className="font-heading text-xl font-bold tracking-tight">
                  {tier.name}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">{tier.desc}</p>

                <div className="mt-6 border-y border-border py-5">
                  <div className="flex items-baseline gap-1.5">
                    <span
                      data-testid={`price-${tier.id}`}
                      className="font-heading text-4xl font-bold tracking-tight"
                    >
                      {tier.prices[duration]}
                    </span>
                    <span className="font-mono text-sm text-muted-foreground">
                      {p.period[duration]}
                    </span>
                  </div>
                  <p className="mt-1.5 text-xs text-muted-foreground/70" data-testid={`price-vat-${tier.id}`}>
                    {t.shop.vat}
                  </p>
                  {duration === "annual" && (
                    <p className="mt-1.5 font-mono text-xs text-muted-foreground">
                      {p.annualEquiv} {tier.prices.monthly}
                      {p.period.monthly}
                    </p>
                  )}
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                    {micro}
                  </p>
                </div>

                <ul className="mt-6 flex-1 space-y-3">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm">
                      <Check
                        className={`mt-0.5 h-4 w-4 flex-shrink-0 ${
                          popular ? "text-blue-500" : "text-muted-foreground"
                        }`}
                      />
                      <span className="text-muted-foreground">{f}</span>
                    </li>
                  ))}
                </ul>

                {(tier.id === "professional" || tier.id === "expert") && (
                  <div
                    data-testid={`pricing-cloud-${tier.id}`}
                    className="mt-4 flex items-start gap-2 rounded-sm border border-blue-600/30 bg-blue-600/[0.06] p-3 text-xs leading-relaxed"
                  >
                    <Cloud className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500" />
                    <span className="text-muted-foreground">{p.cloudAddon.line}</span>
                  </div>
                )}
                {tier.id === "standard" && (
                  <p
                    data-testid="pricing-cloud-standard"
                    className="mt-4 text-xs italic leading-relaxed text-muted-foreground/60"
                  >
                    {p.cloudAddon.standardNote}
                  </p>
                )}

                {tier.note && (
                  <p className="mt-4 text-xs italic leading-relaxed text-muted-foreground/70">
                    {tier.note}
                  </p>
                )}

                <button
                  onClick={goShop}
                  data-testid={`pricing-cta-${tier.id}`}
                  className={`mt-8 inline-flex h-11 items-center justify-center rounded-sm px-5 text-sm font-medium transition-colors duration-200 ${
                    popular
                      ? "bg-blue-600 text-white hover:bg-blue-500"
                      : "border border-border bg-secondary/50 hover:bg-secondary"
                  }`}
                >
                  {p.cta}
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Pricing;
